import React from "react";
import { Form } from "react-crud-form";
import { FormField, FormItem, FormMessage } from "ui/components/form";
import { FormColumns, FormLabel } from "components/form";
import { Input } from "ui/components/input";
import { Textarea } from "ui/components/textarea";
import { useNavigate } from "react-router-dom";
import { clientSchema } from "./schema";
import { useClientForm } from "./useClientForm";
import { CompanyCombobox } from "../../components/directus-combobox";
import { FormActions } from "components/form/actions";
import { PermissionControlledInput } from "permissions/components/input";

const ClientForm = ({ clientId }) => {
  const navigate = useNavigate();
  const { form, requiredFields, handleCreate, handleUpdate, handleRead } =
    useClientForm({
      clientId,
    });

  return (
    <Form
      form={form}
      create={handleCreate}
      read={handleRead}
      update={handleUpdate}
      itemId={clientId || "+"}
      schema={clientSchema}
    >
      {({
        isCreating,
        createItem,
        isNewItem,
        isUpdating,
        updateItem,
        isDirty,
      }) => (
        <div className="space-y-6">
          {/* Name Field */}

          <FormColumns>
            <FormField
              control={form.control}
              name="first_name"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:*" : "update:*"}
                    collection="clients"
                    type="form"
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      First Name
                    </FormLabel>
                    <Input {...field} />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="last_name"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:*" : "update:*"}
                    collection="clients"
                    type="form"
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Last Name
                    </FormLabel>
                    <Input {...field} />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormColumns>

          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem>
                <PermissionControlledInput
                  rule={isNewItem ? "create:*" : "update:*"}
                  collection="clients"
                  type="form"
                >
                  <FormLabel required={requiredFields.includes(field.name)}>
                    Code
                  </FormLabel>
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) =>
                      field.onChange(e.target.value?.toUpperCase() ?? "")
                    }
                    className={
                      (field.className ? field.className + " " : "") +
                      "uppercase"
                    }
                    maxLength={4}
                    placeholder="ROVM, JOCE, etc..."
                  />
                </PermissionControlledInput>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Mobile Number Field */}
          <FormField
            control={form.control}
            name="mobile_number"
            render={({ field }) => (
              <FormItem>
                <PermissionControlledInput
                  rule={isNewItem ? "create:*" : "update:*"}
                  collection="clients"
                  type="form"
                >
                  <FormLabel required={requiredFields.includes(field.name)}>
                    Mobile Number
                  </FormLabel>
                  <Input {...field} />
                </PermissionControlledInput>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Email Field */}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <PermissionControlledInput
                  rule={isNewItem ? "create:*" : "update:*"}
                  collection="clients"
                  type="form"
                >
                  <FormLabel required={requiredFields.includes(field.name)}>
                    Email
                  </FormLabel>
                  <Input type="email" {...field} />
                </PermissionControlledInput>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Company Field */}
          <FormField
            control={form.control}
            name="company_id"
            render={({ field }) => {
              return (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:*" : "update:*"}
                    collection="clients"
                    type="form"
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Company
                    </FormLabel>
                    <CompanyCombobox
                      value={field.value}
                      onChange={field.onChange}
                      searchPlaceholder="Search companies..."
                      addButton={{
                        label: "Add New Company",
                        onClick: () => navigate("/crm/companies/+"),
                      }}
                    />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          {/* Note Field */}
          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <PermissionControlledInput
                  rule={isNewItem ? "create:*" : "update:*"}
                  collection="clients"
                  type="form"
                >
                  <FormLabel required={requiredFields.includes(field.name)}>
                    Notes
                  </FormLabel>
                  <Textarea className="min-h-[100px]" {...field} />
                </PermissionControlledInput>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Client Testimonial Field */}
          <FormField
            control={form.control}
            name="about"
            render={({ field }) => (
              <FormItem>
                <PermissionControlledInput
                  rule={isNewItem ? "create:*" : "update:*"}
                  collection="clients"
                  type="form"
                >
                  <FormLabel required={requiredFields.includes(field.name)}>
                    Client Testimonial
                  </FormLabel>
                  <Textarea className="min-h-[100px]" {...field} />
                </PermissionControlledInput>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Billable Due Days Field */}
          <FormField
            control={form.control}
            name="billable_due_days"
            render={({ field }) => (
              <FormItem>
                <PermissionControlledInput
                  rule={isNewItem ? "create:*" : "update:*"}
                  collection="clients"
                  type="form"
                >
                  <FormLabel required={requiredFields.includes(field.name)}>
                    Due Days
                  </FormLabel>
                  <Input
                    type="number"
                    value={field.value ?? ""}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === ""
                          ? null
                          : Number(e.target.value)
                      )
                    }
                  />
                </PermissionControlledInput>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormActions
            isNewItem={isNewItem}
            isCreating={isCreating}
            isUpdating={isUpdating}
            isDirty={isDirty}
            createItem={() => createItem().then(() => navigate(-1))}
            updateItem={() => updateItem().then(() => navigate(-1))}
            showCancel={false}
            collection="clients"
            rule={isNewItem ? "create:*" : "update:*"}
          />
        </div>
      )}
    </Form>
  );
};

export default ClientForm;
